/* eslint-disable eqeqeq */
/**
 * COMBO LOUVER (KA-5245) — state shape + pure view helpers.
 * Sub-phase A1.1 (combo-mode detection + read-only render).
 *
 * A combo louver is NOT a new accessory model: it is the ordinary
 * `AHUAccessoryLouver` with `is_combo = true` (inventory §1). It shares the
 * model, the API, the board row and the selector dialog with a plain louver and
 * switches both into a different mode:
 *  - `master_louver_id` on the parent is ALWAYS null — all three faces share one
 *    catalog identity (mfg / series / blade orientation / frame material) carried
 *    in `louver_params`; only the SIZES are per-face (inventory §1 table);
 *  - `width_calc`/`height_calc` become the ENCLOSURE (see mountingModel
 *    accessorySize(), the single footprint choke point — inventory §10);
 *  - the faces are exactly three, `'front' | 'left' | 'right'`
 *    (models_accessory_louver.py:1958-1959 FACE_CHOICES).
 *
 * This module holds only the pieces A1.1 needs: the per-face state bag (legacy
 * `self._comboFaces`), the `aw_dims` option-list bag (legacy `self._comboAwDims`,
 * FILLED by A1.2), the template view-model (`_getComboFacesView`) and the shared
 * smartlist counter. Everything stateful/asynchronous — the aw_dims fetcher, the
 * per-face `master_louver_id` lookups, the resize engine, the handlers and the
 * persist — lands in A1.2 … A1.6 (seams marked below).
 *
 * Legacy source: django/static/mvc/component_selector/components/accessory/view/
 * accessory.selector.js (the `// --- Combo Louver (KA-5245) ---` block, :893+).
 */

export type ComboFace = 'front' | 'left' | 'right';

/** FACE_CHOICES order — the render order of the three face rows. */
export const COMBO_FACES: ComboFace[] = ['front', 'left', 'right'];

/** Row labels — `_getComboFacesView` :925. */
const COMBO_FACE_LABELS: Record<ComboFace, string> = {
  front: 'Front',
  left: 'Left Side',
  right: 'Right Side',
};

/**
 * A combo louver's desired-face-velocity default is 500 fpm, NOT the view's
 * 1200 (accessory.selector.js:115-123 + :350). Inventory §1 field table.
 */
export const COMBO_DEFAULT_FACE_VELOCITY = 500;

/**
 * One face's client state — legacy `self._comboFaces[face]`.
 *
 * ★ `checked` vs `active` GENUINELY DIFFER and both are modelled here
 * (inventory §1): `checked` is the user's checkbox (user intent, the only thing
 * the client owns and the only thing sent on the wire — `_sync_combo` whitelists
 * `checked` and never accepts `active`, louver_viewset.py:135). `active` is
 * DERIVED SERVER-SIDE as `checked and aw_width and aw_height` and comes back on
 * the response: the sides-first auto-size can REMOVE the front louver (the sides
 * over-provide the required area) — the checkbox stays `checked`, `active` goes
 * false and the airways go null (models_accessory_louver.py:1952-1956).
 * Consumers therefore differ: the dialog checkbox reads `checked`; the 3-D face
 * gate reads `active` (canvas.view3d.js:3244 — A2.3).
 */
export interface ComboFaceState {
  /** the checkbox — user intent. */
  checked: boolean;
  /** server-derived `checked && aw_width && aw_height`; never sent. */
  active?: boolean;
  /**
   * '' (not "-") when unset — legacy `_initComboFaces` normalises null → ''.
   * ⚠ `null` is also legal at runtime and A1.2 relies on it: legacy's
   * `_resetComboFaceAwSelections` writes a bare `null` when a saved size is no
   * longer among the options (:1457/:1461), and `onComboFaceFieldChange` writes
   * `null` for a cleared select (:1135). Every consumer is `== null`-safe and
   * `_getComboFacesView` renders null as ''.
   */
  aw_width: number | '' | null;
  aw_height: number | '' | null;
  /** resolved per-face catalog part (A1.2 `_recalcComboMasterLouverId`). */
  master_louver_id?: number | null;
  /** serializer SerializerMethodField (master_louver.frame_depth fallback). */
  depth?: number | null;
  /**
   * ⚠ DEAD-2: `frame_depth` is NOT a per-face serializer field
   * (louver_serializers.py:12-13) — the selector stores it off the response
   * (:1301) and it is ALWAYS null. Kept so the ported response-application in
   * A1.3 reads 1:1 with legacy.
   */
  frame_depth?: number | null;
  /** A1.2/A1.4 — "this size has no matching catalog louver" (.not-valid). */
  noMatch?: boolean;
  /** A1.2 in-flight `/louvers/all/` lookup URL (dedup cache). */
  pendingLouverUrl?: string | null;
  /**
   * ★ Q2 MIRROR — `_restoreMissingSelectOption`'s LIFETIME, not just its effect.
   * Legacy appends the "saved value is not in the current option list" option
   * inside `_updateComboAwSelects` (:1494/:1505), which runs ONLY from
   * `_fetchComboAwDims`. It is a one-shot DOM append: any later `render()`
   * rebuilds the selects out of `_comboAwDims` alone and the appended option is
   * GONE. So a value written AFTER that pass — the server's per-face echo — is
   * NOT restored and the select renders BLANK. That is exactly the measured Q2
   * outcome on the smartlist-pick path: `&side=true` is empty at depth 12, the
   * faces do hold 22 × 62, and both side selects show nothing.
   * Recomputing the extra option live on every render (what a declarative port
   * does naturally) silently FIXES Q2 and diverges from legacy. So these two
   * fields hold the snapshot taken at fetch time
   * (`snapshotComboMissingOptions`), cleared by the engine's `applyFaceEcho`,
   * and ONLY they feed `ComboFaceView.aw_*_extra`.
   */
  aw_width_extra?: { value: string; label: string } | null;
  aw_height_extra?: { value: string; label: string } | null;
}

export type ComboFacesState = Record<ComboFace, ComboFaceState>;

/**
 * `_initComboFaces` :894-921 — all three faces default CHECKED with blank
 * airways; any `combo_faces[]` row on the model overwrites its face. A fresh
 * combo legitimately has `combo_faces: []` (the rows are created lazily by
 * `get_or_create` on the first PATCH naming the face, louver_viewset.py:122-123),
 * so the defaults are what a just-inserted combo renders with (inventory §3.3).
 */
export function initComboFaces(model: Record<string, unknown>): ComboFacesState {
  const faces: ComboFacesState = {
    front: { checked: true, aw_width: '', aw_height: '' },
    left: { checked: true, aw_width: '', aw_height: '' },
    right: { checked: true, aw_width: '', aw_height: '' },
  };
  const existing = model.combo_faces;
  if (Array.isArray(existing)) {
    (existing as Array<Record<string, unknown>>).forEach((f) => {
      const face = String(f.face ?? '') as ComboFace;
      if (!faces[face]) return;
      faces[face] = {
        checked: f.checked !== false,
        aw_width: f.aw_width == null ? '' : (f.aw_width as number),
        aw_height: f.aw_height == null ? '' : (f.aw_height as number),
        master_louver_id: (f.master_louver_id as number | null) || null,
        active: !!f.active,
        // Louver frame depth (KA-5253): kept per-face so the 3-D canvas can
        // model each face louver at its own depth instead of falling back to
        // the combo's shared depth.
        depth: f.depth == null ? null : (f.depth as number),
        frame_depth: f.frame_depth == null ? null : (f.frame_depth as number),
      };
    });
  }
  return faces;
}

/** One `aw_dims` option-list pair (flat, independently sorted, NOT correlated —
 * inventory §3.1 R5). */
export interface ComboAwDimsSet {
  aw_widths: number[];
  aw_heights: number[];
}

/** Front face is bound by `combo_louver_enclosure_width_max`; left/right share
 * one `&side=true` fetch bound by the box depth (`_comboAwDimsKeyForFace` :960,
 * KA-5250). */
export type ComboAwDimsKey = 'front' | 'side';

export type ComboAwDims = Record<ComboAwDimsKey, ComboAwDimsSet>;

/** `self._comboAwDims` initial value — accessory.selector.js:137-140. */
export const emptyComboAwDims = (): ComboAwDims => ({
  front: { aw_widths: [], aw_heights: [] },
  side: { aw_widths: [], aw_heights: [] },
});

/** `_comboAwDimsKeyForFace` :960-962. */
export const comboAwDimsKeyForFace = (face: ComboFace): ComboAwDimsKey =>
  face === 'front' ? 'front' : 'side';

/**
 * `_comboAutoSizeEnabled` — accessory.selector.js:144-146. Default ON: Backbone
 * `has('auto_size')` is `get(...) != null`, so an absent/null flag means ON.
 */
export function comboAutoSizeEnabledFromModel(model: Record<string, unknown>): boolean {
  const raw = model.auto_size;
  return raw != null ? !!raw : true;
}

/** One face row as the template consumes it — `_getComboFacesView` :923-956. */
export interface ComboFaceView {
  face: ComboFace;
  label: string;
  checked: boolean;
  /** computed by legacy but NEVER read by louver.selector.tmpl — it belongs to
   * the DEAD-5 per-face-smartlist machinery (`_comboFaceHasLouver`). Kept for a
   * 1:1 port; `smartlist_count` (:953) is deliberately NOT ported (DEAD-9). */
  has_louver: boolean;
  checkbox_disabled: boolean;
  inputs_disabled: boolean;
  aw_width: number | '';
  aw_height: number | '';
  aw_widths: number[];
  aw_heights: number[];
  /**
   * A1.2 — `_restoreMissingSelectOption` :602-619, reached from
   * `_updateComboAwSelects` :1494 / :1505. The saved size survived
   * `_resetComboFaceAwSelections` (the shared mfg/series identity went CMS-
   * inactive) but is not among the current options: legacy appends it back as a
   * SELECTED + DISABLED `<option>` labelled with `Number(v).toFixed(2)` so the
   * user still sees what was chosen without being able to re-pick it. React's
   * selects are declarative, so the extra option is computed here instead of
   * patched into the DOM. `null` = nothing to restore (the common case).
   */
  aw_width_extra: { value: string; label: string } | null;
  aw_height_extra: { value: string; label: string } | null;
  /**
   * A1.2 — `_setComboFaceInvalid` :1718-1725 / re-applied by
   * `_applyComboValidation` :1077-1082. TRUE once this face's fully-specified
   * params matched NO catalog louver, and it marks BOTH of the face's selects
   * `.not-valid` (red text, `rating_mode.scss:2162-2164`). Legacy keeps it on
   * `_comboFaces[face].noMatch` precisely so it survives `render()`.
   * ⚠ `_isSelectValid`'s combo arm reads these back off the DOM
   * (`.combo-aw-select.not-valid`, :3590) — that gate is A1.4.
   */
  not_valid: boolean;
}

/**
 * `_getComboFacesView` :923-956 — the three face rows.
 *  - `checkbox_disabled`: the LAST remaining checked face cannot be unchecked
 *    (template guard; the JS handler guards again at :1839-1847 — A1.4).
 *  - `inputs_disabled`: W x H editable only when the face is checked AND
 *    auto-size is OFF.
 *  - the airways are DISPLAYED only while the face is checked; the stored values
 *    stay on the face state so re-checking restores them.
 */
export function getComboFacesView(
  faces: ComboFacesState | null,
  awDims: ComboAwDims,
  autoSizeEnabled: boolean
): ComboFaceView[] {
  const bag = faces ?? ({} as Partial<ComboFacesState>);
  const checkedCount = COMBO_FACES.filter((f) => !bag[f] || bag[f]!.checked !== false).length;
  return COMBO_FACES.map((face) => {
    const f = bag[face] ?? ({ checked: true, aw_width: '', aw_height: '' } as ComboFaceState);
    const checked = f.checked !== false;
    const hasLouver = checked && f.aw_width != null && f.aw_width !== '';
    const dims = awDims[comboAwDimsKeyForFace(face)] ?? { aw_widths: [], aw_heights: [] };
    const aw_widths = dims.aw_widths ?? [];
    const aw_heights = dims.aw_heights ?? [];
    const aw_width = checked && f.aw_width != null ? f.aw_width : '';
    const aw_height = checked && f.aw_height != null ? f.aw_height : '';
    return {
      face,
      label: COMBO_FACE_LABELS[face],
      checked,
      has_louver: hasLouver,
      checkbox_disabled: checked && checkedCount <= 1,
      inputs_disabled: !checked || autoSizeEnabled,
      aw_width,
      aw_height,
      aw_widths,
      aw_heights,
      // ★ Q2: the SNAPSHOT taken by `snapshotComboMissingOptions` at aw_dims
      // fetch time — deliberately NOT `missingOptionFor(aw_width, aw_widths)`
      // recomputed here. See `ComboFaceState.aw_width_extra`.
      aw_width_extra: (checked ? f.aw_width_extra : null) ?? null,
      aw_height_extra: (checked ? f.aw_height_extra : null) ?? null,
      not_valid: !!f.noMatch,
    };
  });
}

/**
 * A2.1 — the per-face payload the 3-D canvas gets from the SELECTOR's mini
 * preview (`_updateCanvasPreview` :3415-3428, verbatim).
 *
 * The staging model's own `combo_faces` can be stale or empty while editing (the
 * rows only exist server-side once a PATCH names the face), so the preview feeds
 * the canvas the CURRENT per-face working state instead (KA-5249).
 *
 * ★ `active` is recomputed CLIENT-side here as `checked && !!w && !!h` — the
 * server's `active` is deliberately ignored on this path, because the whole
 * point is to show the box the user is building before the round trip lands.
 * The canvas face gate reads exactly this field (canvas.view3d.js:3244), so a
 * face whose checkbox is on but whose airways are blank does NOT draw.
 *
 * `depth` / `frame_depth` are carried through for the canvas's per-face depth
 * ladder (KA-5253); `frame_depth` is always null (inventory DEAD-2).
 */
export function buildComboCanvasFaces(faces: ComboFacesState | null): Array<{
  face: ComboFace;
  checked: boolean;
  active: boolean;
  aw_width: number | null;
  aw_height: number | null;
  depth: number | null | undefined;
  frame_depth: number | null | undefined;
}> {
  const bag = faces ?? ({} as Partial<ComboFacesState>);
  return COMBO_FACES.map((face) => {
    const f = bag[face] ?? ({} as ComboFaceState);
    const checked = f.checked !== false;
    // legacy strips the thousands separator before parseFloat — the values can
    // arrive as formatted strings straight off a select/input.
    const w = f.aw_width ? parseFloat(String(f.aw_width).replace(/,/g, '')) : null;
    const h = f.aw_height ? parseFloat(String(f.aw_height).replace(/,/g, '')) : null;
    return {
      face,
      checked,
      active: checked && !!w && !!h,
      aw_width: w,
      aw_height: h,
      depth: f.depth,
      frame_depth: f.frame_depth,
    };
  });
}

/** `_restoreMissingSelectOption` :602-619 — the declarative half (see the
 * `aw_width_extra` note on `ComboFaceView`). A no-op for null/'' and when the
 * option is already present, so it never duplicates. */
function missingOptionFor(
  saved: number | '',
  options: number[]
): { value: string; label: string } | null {
  if (saved === '' || saved == null) return null;
  const savedStr = String(saved);
  if (options.some((o) => String(o) === savedStr)) return null;
  const n = Number(saved);
  // legacy passes `Number(v).toFixed(2)` as savedName and falls back to `#<id>`.
  return { value: savedStr, label: Number.isFinite(n) ? n.toFixed(2) : `#${savedStr}` };
}

/** A `cms.combo_smartlist` row — 43 distinct catalog PRODUCT LINES (mfg /
 * series / frame material / blade orientation), no sizes (inventory §2.3). */
export interface ComboSmartlistRow {
  mfg_id?: number;
  series_id?: number;
  frame_mtl_id?: number;
  blade_orientation_id?: number;
  mfg_name?: string;
  series_name?: string;
  frame_mtl_name?: string;
  blade_orientation_name?: string;
  [key: string]: unknown;
}

/**
 * `_comboSmartlistFilteredItems` :968-978 — the shared combo smartlist narrowed
 * to whatever the combo's shared dropdowns already have selected.
 */
export function comboSmartlistFilteredItems(
  cms: Record<string, unknown> | null,
  params: Record<string, unknown>
): ComboSmartlistRow[] {
  const rows = (cms?.combo_smartlist as ComboSmartlistRow[] | undefined) ?? [];
  return rows.filter((r) => {
    if (params.mfg_id && r.mfg_id != params.mfg_id) return false;
    if (params.series_id && r.series_id != params.series_id) return false;
    if (params.frame_mtl_id && r.frame_mtl_id != params.frame_mtl_id) return false;
    if (params.blade_orientation_id && r.blade_orientation_id != params.blade_orientation_id)
      return false;
    return true;
  });
}

/**
 * `_comboLouverCounts` :985-989 — the "Louvers x / y" header counter.
 * ⚠ QUIRK Q14 (inventory §8): this is `matches / total` over `combo_smartlist`,
 * so `x` ALWAYS equals `combo_smartlist_items.length` — measured `43/43` on open
 * and `1/43` after a pick. It is NOT a per-part catalog count.
 */
export function comboLouverCounts(
  cms: Record<string, unknown> | null,
  params: Record<string, unknown>
): { count: number; total: number } {
  const total = (cms?.combo_smartlist as ComboSmartlistRow[] | undefined) ?? [];
  return { count: comboSmartlistFilteredItems(cms, params).length, total: total.length };
}

/* ===========================================================================
 * A1.2 — the shared identity, the two URL builders, the option-list reconcile
 *        and the per-face lookup predicates. All PURE; the async orchestration
 *        lives in comboLouverEngine.ts.
 * ======================================================================== */

/**
 * The FOUR shared-identity params (`_fetchComboAwDims` :1357-1361). All three
 * faces share ONE catalog identity — manufacturer / series / blade orientation /
 * frame material, carried in `louver_params`. Only the SIZES are per-face, which
 * is why the parent louver's `master_louver_id` is always null (inventory §1).
 */
export interface ComboSharedIdentity {
  mfg_id: number | null;
  series_id: number | null;
  blade_orientation_id: number | null;
  frame_mtl_id: number | null;
}

export function comboSharedIdentity(params: Record<string, unknown>): ComboSharedIdentity {
  return {
    mfg_id: (params.mfg_id as number) || null,
    series_id: (params.series_id as number) || null,
    blade_orientation_id: (params.blade_orientation_id as number) || null,
    frame_mtl_id: (params.frame_mtl_id as number) || null,
  };
}

/** All four present → the identity is fully picked and aw_dims can be fetched. */
export const comboIdentityComplete = (i: ComboSharedIdentity): boolean =>
  !!(i.mfg_id && i.series_id && i.blade_orientation_id && i.frame_mtl_id);

/**
 * `_fetchComboAwDims` :1384-1389 — the aw_dims BASE url, to which the caller
 * appends `&front=true` or `&side=true`.
 *
 * ★ Built by STRING CONCATENATION on purpose, exactly as legacy does. The
 * enclosure dimensions are what BOUND the returned lists, so any resize
 * invalidates them; and when a dimension is null the query carries the literal
 * `"null"`, which makes the endpoint 500 (QUIRK Q5 / RISK R1 — reproduced 6×
 * after Reset). A port that omitted the params, or coerced them to numbers,
 * would DIVERGE from legacy: mirror the failure, do not defend against it.
 *
 * Measured bounding to reproduce (inventory §3.1): `auto_size=true,width=51` →
 * 277 front widths (CMS max is NULL on dev3 ⇒ unbounded); `auto_size=false,
 * width=51` → 38; `width=63` → 50; `&side=true` at `depth=12` → 0, `depth=24` →
 * 11, `depth=2430` → 277.
 */
export function buildComboAwDimsUrl(o: {
  cmsMfgId: number;
  identity: ComboSharedIdentity;
  depth: number | null;
  autoSize: boolean;
  width: number | null;
  height: number | null;
}): string {
  const qs =
    `mfg_id=${o.identity.mfg_id}&series_id=${o.identity.series_id}` +
    `&blade_orientation_id=${o.identity.blade_orientation_id}&frame_mtl_id=${o.identity.frame_mtl_id}` +
    `&depth=${o.depth}` +
    `&auto_size=${o.autoSize}` +
    `&width=${o.width}&height=${o.height}`;
  return `/api/v1.0/cms/${o.cmsMfgId}/v1.0.0/ahu/louvers/aw_dims/?${qs}`;
}

/**
 * `_comboMasterLouverUrl` :1699-1713 — one face's `/louvers/all/` lookup URL.
 * Param ORDER is legacy's verbatim (mfg_id, blade_orientation_id, the EMPTY
 * `page=`, series_id, frame_mtl_id, aw_width, aw_height) so the wire URL diffs
 * byte-identically against legacy (measured §3.1 R7).
 */
export function buildComboMasterLouverUrl(o: {
  cmsMfgId: number;
  params: Record<string, unknown>;
  face: ComboFaceState;
}): string {
  const enc = (v: unknown) => encodeURIComponent(String(v));
  const qs = [
    `mfg_id=${enc(o.params.mfg_id)}`,
    `blade_orientation_id=${enc(o.params.blade_orientation_id)}`,
    'page=',
    `series_id=${enc(o.params.series_id)}`,
    `frame_mtl_id=${enc(o.params.frame_mtl_id)}`,
    `aw_width=${enc(o.face.aw_width)}`,
    `aw_height=${enc(o.face.aw_height)}`,
  ].join('&');
  return `/api/v1.0/cms/${o.cmsMfgId}/v1.0.0/ahu/louvers/all/?${qs}`;
}

/**
 * `_comboFaceLouverParamsComplete` :1689-1696 — a face is eligible for a
 * `master_louver_id` lookup only once its OWN W×H are chosen AND the shared
 * identity is fully specified. Unchecked faces never look up.
 */
export function comboFaceLouverParamsComplete(
  face: ComboFaceState | undefined,
  params: Record<string, unknown>
): boolean {
  const f = face ?? ({} as ComboFaceState);
  if (f.checked === false) return false;
  if (f.aw_width == null || f.aw_width === '' || f.aw_height == null || f.aw_height === '')
    return false;
  return comboIdentityComplete(comboSharedIdentity(params));
}

/**
 * `_comboSharedIdentityActive` :1421-1429 — true only while the saved shared
 * mfg_id/series_id are still present in the CMS-active lists. FALSE means an
 * admin deactivated one of them, which callers use to tell "the user's own
 * change invalidated this face's size" (→ clear it) apart from "same pick, just
 * gone inactive upstream" (→ preserve it and show it disabled).
 */
export function comboSharedIdentityActive(
  cms: Record<string, unknown> | null,
  params: Record<string, unknown>
): boolean {
  const toId = (v: unknown) => (v != null && v !== '' ? parseInt(String(v), 10) : null);
  const mfgId = toId(params.mfg_id);
  const seriesId = toId(params.series_id);
  const mfgRows = (cms?.manufacturer as Array<{ id: number }> | undefined) ?? [];
  const seriesRows = (cms?.series as Array<{ id: number }> | undefined) ?? [];
  const mfgActive = mfgId == null || mfgRows.some((m) => m.id === mfgId);
  const seriesActive = seriesId == null || seriesRows.some((s) => s.id === seriesId);
  return mfgActive && seriesActive;
}

/**
 * `_resetComboFaceAwSelections` :1442-1467 — MUTATES the face bag. Drops any
 * face size that no longer appears among the current options (covers both "the
 * options went empty" and "a filter change dropped a previously valid size") —
 * but only while the shared identity is still CMS-active; when it is not, the
 * saved size is LEFT ALONE and `ComboFaceView.aw_*_extra` re-adds it as a
 * disabled fallback instead of losing the user's prior pick.
 *
 * ★ With auto-size OFF it SNAPS TO THE LARGEST remaining option instead of
 * clearing: auto-size is what re-derives a value when it is on, and with it off a
 * manual enclosure tweak would otherwise force the user to reselect every face by
 * hand.
 *
 * Every path clears `master_louver_id` — any change to W/H invalidates the prior
 * catalog pick. Legacy then calls `_updateComboFaceVelocity()`; that is an
 * effect, so the engine does it (this stays pure w.r.t. the network/DOM).
 */
export function resetComboFaceAwSelections(
  faces: ComboFacesState | null,
  awDims: ComboAwDims,
  autoSizeEnabled: boolean,
  identityActive: boolean
): void {
  if (!faces) return;
  const autoSizeOff = !autoSizeEnabled;
  COMBO_FACES.forEach((face) => {
    const f = faces[face];
    if (!f) return;
    const dims = awDims[comboAwDimsKeyForFace(face)] ?? { aw_widths: [], aw_heights: [] };
    const widthOptions = (dims.aw_widths ?? []).map(Number);
    const heightOptions = (dims.aw_heights ?? []).map(Number);
    const widths = new Set(widthOptions.map(String));
    const heights = new Set(heightOptions.map(String));
    f.master_louver_id = null;
    let changed = false;
    if (identityActive && f.aw_width != null && f.aw_width !== '' && !widths.has(String(f.aw_width))) {
      f.aw_width = autoSizeOff && widthOptions.length ? Math.max(...widthOptions) : null;
      changed = true;
    }
    if (
      identityActive &&
      f.aw_height != null &&
      f.aw_height !== '' &&
      !heights.has(String(f.aw_height))
    ) {
      f.aw_height = autoSizeOff && heightOptions.length ? Math.max(...heightOptions) : null;
      changed = true;
    }
    if (changed) f.master_louver_id = null;
  });
}

/**
 * ★ `_updateComboAwSelects`' restore half (:1494/:1505) — the ONE point at which
 * legacy appends a "saved value not in the current option list" option. Called
 * only from the engine's aw_dims-fetch tail, immediately after
 * `resetComboFaceAwSelections`, so the snapshot reflects the freshly-fetched
 * lists. Everything else leaves the previous snapshot alone or clears it
 * (`applyFaceEcho`), which is what makes Q2 reproduce — see
 * `ComboFaceState.aw_width_extra`.
 */
export function snapshotComboMissingOptions(
  faces: ComboFacesState | null,
  awDims: ComboAwDims
): void {
  if (!faces) return;
  COMBO_FACES.forEach((face) => {
    const f = faces[face];
    if (!f) return;
    const dims = awDims[comboAwDimsKeyForFace(face)] ?? { aw_widths: [], aw_heights: [] };
    f.aw_width_extra = missingOptionFor(
      f.aw_width == null ? '' : f.aw_width,
      (dims.aw_widths ?? []).map(Number)
    );
    f.aw_height_extra = missingOptionFor(
      f.aw_height == null ? '' : f.aw_height,
      (dims.aw_heights ?? []).map(Number)
    );
  });
}

/**
 * `_comboActualFaceVelocity` :1161-1172 — airflow ÷ the summed CHECKED face
 * area (in²→ft²), rounded. Client-side for immediacy; the server's value from
 * `/with_position` supersedes it.
 */
export function comboActualFaceVelocity(
  faces: ComboFacesState | null,
  airflow: unknown
): number | null {
  const flow = parseFloat(String(airflow));
  if (!flow) return null;
  let areaFt2 = 0;
  COMBO_FACES.forEach((face) => {
    const f = faces?.[face] ?? ({} as ComboFaceState);
    const w = parseFloat(String(f.aw_width));
    const h = parseFloat(String(f.aw_height));
    if (f.checked !== false && w && h) areaFt2 += (w * h) / 144;
  });
  return areaFt2 ? Math.round(flow / areaFt2) : null;
}

/**
 * `_applyComboSmartlistPick` :1590-1612 — the params half. Sets the SHARED
 * identity from the clicked row and caches the display names alongside the ids
 * (KA-5250), which is what lets a later CMS deactivation still show a real name.
 * Airway sizes are untouched (they are per-face). Returns the new params object.
 */
export function applyComboSmartlistPickParams(
  params: Record<string, unknown>,
  row: ComboSmartlistRow,
  smartlist: ComboSmartlistRow[]
): Record<string, unknown> {
  const num = (v: unknown) => (v == null || v === '' ? null : parseInt(String(v), 10));
  const mfgId = num(row.mfg_id);
  const seriesId = num(row.series_id);
  const bladeId = num(row.blade_orientation_id);
  const frameId = num(row.frame_mtl_id);
  const out = { ...params };
  out.mfg_id = mfgId;
  out.series_id = seriesId;
  if (bladeId) out.blade_orientation_id = bladeId;
  else delete out.blade_orientation_id;
  if (frameId) out.frame_mtl_id = frameId;
  else delete out.frame_mtl_id;
  // legacy re-finds the row in cms.combo_smartlist rather than trusting the
  // clicked element's data-* attrs for the NAMES (:1602-1606).
  const picked = smartlist.find(
    (r) =>
      r.mfg_id == mfgId &&
      r.series_id == seriesId &&
      (r.blade_orientation_id || null) == (bladeId || null) &&
      (r.frame_mtl_id || null) == (frameId || null)
  );
  out.mfg_name = picked ? picked.mfg_name : null;
  out.series_name = picked ? picked.series_name : null;
  if (bladeId) out.blade_orientation_name = picked ? picked.blade_orientation_name : null;
  else delete out.blade_orientation_name;
  if (frameId) out.frame_mtl_name = picked ? picked.frame_mtl_name : null;
  else delete out.frame_mtl_name;
  return out;
}

/**
 * `changeParamsField` :2442-2452 — combo-only: cache the chosen `<option>`'s
 * display text alongside its id so a saved selection still shows a real name if
 * CMS later deactivates it (`_restoreComboLouverSelects` :624-634).
 */
export const COMBO_NAME_PARAM: Record<string, string> = {
  mfg_id: 'mfg_name',
  series_id: 'series_name',
  frame_mtl_id: 'frame_mtl_name',
  blade_orientation_id: 'blade_orientation_name',
};

/**
 * `_applyEnclosureDefault` :1089-1109 — silent staging defaults, run ONCE at
 * init for a combo (:147).
 *
 * ⚠ Two legacy facts reproduced verbatim:
 *  1. it runs at :147, BEFORE `fn['current.cms.louver']` resolves (:201), so
 *     `self.cms` is undefined and the `combo_louver_enclosure_width_default`
 *     branch can NEVER fire at init. Pass the same empty CMS here.
 *  2. the material fallback reads `section.outerliner_id`. In practice both
 *     branches are no-ops on a fresh combo: the server already defaults
 *     `enclosure_material_id` (697) and `enclosure_depth` (12) in `save()`
 *     (models_accessory_louver.py:1826-1866; measured POST response, §4 W1).
 */
export function applyEnclosureDefaults(
  model: Record<string, unknown>,
  cms: Record<string, unknown>,
  section: Record<string, unknown>
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  // Enclosure material defaults to the tunnel casing outer skin.
  if (!model.enclosure_material_id) {
    const outerliner = section.outerliner_id;
    if (outerliner) out.enclosure_material_id = outerliner;
  }
  // Depth defaults to (and may not go below) the 12" minimum.
  const depth = model.enclosure_depth;
  if (depth == null || depth === '') out.enclosure_depth = 12;
  // Enclosure width defaults to the CMS-set default when unset.
  const w = model.enclosure_width;
  if ((w == null || w === '') && cms.combo_louver_enclosure_width_default != null) {
    out.enclosure_width = cms.combo_louver_enclosure_width_default;
  }
  return out;
}

/* ===========================================================================
 * A1.4 — validation, the Select gate and the Reset mutators. All PURE; the
 *        handlers and the DOM `.not-valid` pass live in comboLouverEngine.ts.
 * ======================================================================== */

/**
 * `_comboFacesSized` :3553-3561 — are the CHECKED faces sufficiently sized?
 *
 * ★★ QUIRK Q8 (the prompt's "Q6") — MIRRORED, NOT FIXED. With auto-size ON it
 * requires only `.some()` checked face to be sized; with auto-size OFF it
 * requires `.every()`. The stated rationale is "the sizing engine fills in every
 * checked face automatically, so one sized face is enough to confirm a round trip
 * landed" — but the measured consequence is a BUG: after a smartlist pick at
 * depth 12 the `&side=true` option list comes back EMPTY (Q2), so both side
 * selects render blank, and `.some()` still lets `#select` become clickable. A
 * port that used `.every()` on both branches would silently fix it and diverge.
 * Parity-only: legacy bugs are requirements.
 */
export function comboFacesSized(
  faces: ComboFacesState | null,
  autoSizeEnabled: boolean
): boolean {
  const bag = faces ?? ({} as Partial<ComboFacesState>);
  const checkedFaces = COMBO_FACES.map((f) => bag[f]).filter(
    (f): f is ComboFaceState => !!f && f.checked !== false
  );
  if (!checkedFaces.length) return false;
  const isSized = (f: ComboFaceState) => !!(f.aw_width && f.aw_height);
  return autoSizeEnabled ? checkedFaces.some(isSized) : checkedFaces.every(isSized);
}

/**
 * `_applyComboValidation`'s face half (:1069-1073) — the `.combo-face-row`
 * `.not-valid` flag is "nothing is checked at all".
 * ⚠ Legacy tests `_comboFaces[f].checked` TRUTHILY here (and in
 * `toggleComboFace`'s last-face guard, :1840-1842), not `!== false` as
 * `_getComboFacesView` does. Kept verbatim; the two agree in practice because
 * every face state carries an explicit boolean.
 */
export const comboAnyFaceChecked = (faces: ComboFacesState | null): boolean =>
  COMBO_FACES.some((f) => !!faces?.[f]?.checked);

/**
 * `_isSelectValid`'s combo arm reads the flag off the DOM
 * (`self.$el.find('.combo-aw-select.not-valid').length > 0`, :3590). React's
 * face selects carry `.not-valid` declaratively off `noMatch`
 * (`ComboFaceView.not_valid`), so the state IS the DOM here — and legacy's query
 * likewise covers all three rendered faces regardless of `checked`
 * (`toggleComboFace` clears `noMatch` when a face is unchecked).
 */
export const comboAnyFaceInvalid = (faces: ComboFacesState | null): boolean =>
  COMBO_FACES.some((f) => !!faces?.[f]?.noMatch);

/**
 * `_isSelectValid` :3577-3591, the combo arm. A combo louver has NO single
 * `master_louver_id` (§1 — the faces carry the catalog identity), so the
 * plain-louver `master_louver_id != null && blade_direction_id != null` test can
 * never pass for one; this replaces it.
 *
 * Four conjuncts, verbatim:
 *  1. the SHARED identity is fully picked (mfg + series + blade orientation +
 *     frame material, out of `louver_params`);
 *  2. `comboFacesSized()` — ⚠ Q8 lives there;
 *  3. at least one `cms.combo_smartlist` row still matches the current filter
 *     ("an empty Smartlist means the chosen combo doesn't exist") — this is
 *     `_comboLouverCounts(params).count > 0`, i.e. Q14's `x` in "Louvers x / y";
 *  4. no face flagged `.not-valid` (a size with no matching catalog louver).
 */
export function comboIsSelectValid(o: {
  cms: Record<string, unknown> | null;
  params: Record<string, unknown>;
  faces: ComboFacesState | null;
  autoSizeEnabled: boolean;
}): boolean {
  const hasIdentity = comboIdentityComplete(comboSharedIdentity(o.params));
  const facesSized = comboFacesSized(o.faces, o.autoSizeEnabled);
  const hasSmartlistMatch = comboLouverCounts(o.cms, o.params).count > 0;
  const hasInvalidFace = comboAnyFaceInvalid(o.faces);
  return !!(hasIdentity && facesSized && hasSmartlistMatch && !hasInvalidFace);
}

/**
 * `resetComboFace` :1512-1541, the params half — the eight keys it deletes: the
 * four shared-identity ids AND their four cached `*_name` twins (KA-5250). The
 * enclosure `material_id`/`material_name` pair is deliberately NOT cleared
 * (legacy lists only these eight).
 */
export const COMBO_RESET_PARAM_KEYS = [
  'mfg_id',
  'series_id',
  'blade_orientation_id',
  'frame_mtl_id',
  'mfg_name',
  'series_name',
  'blade_orientation_name',
  'frame_mtl_name',
] as const;

export function resetComboIdentityParams(
  params: Record<string, unknown>
): Record<string, unknown> {
  const out = { ...params };
  COMBO_RESET_PARAM_KEYS.forEach((k) => {
    delete out[k];
  });
  return out;
}

/**
 * `resetComboFace` :1524-1531, the faces half — MUTATES the bag. Every face's
 * airway pair, catalog pick, `.not-valid` flag and in-flight lookup token are
 * cleared; `checked` is deliberately LEFT ALONE (Reset unsizes the combo, it does
 * not re-check the faces), and neither is the enclosure DEPTH.
 *
 * ★ The `aw_*_extra` snapshots are cleared too: they are the one-shot
 * "saved value is not in the current option list" options taken at aw_dims fetch
 * time, and the value they described is gone.
 */
export function resetComboFaces(faces: ComboFacesState | null): void {
  if (!faces) return;
  COMBO_FACES.forEach((face) => {
    const f = faces[face];
    if (!f) return;
    f.aw_width = null;
    f.aw_height = null;
    f.master_louver_id = null;
    f.noMatch = false;
    f.pendingLouverUrl = null;
    f.aw_width_extra = null;
    f.aw_height_extra = null;
  });
}

/* ===========================================================================
 * WAVE MAP — what is built, and where the remaining waves plug in.
 * ===========================================================================
 *
 * A1.1 — DONE (wave 1): combo-mode detection + the read-only render.
 *
 * A1.2 — DONE (wave 2): `aw_dims` + the shared smartlist + per-face
 *   `master_louver_id`.
 *   · `comboLouverApi.ts` (`getComboAwDims` / `getComboMasterLouvers`);
 *     `buildComboAwDimsUrl` + `buildComboMasterLouverUrl` above; the TWO PARALLEL
 *     `&front=true`/`&side=true` calls, the `_comboAwDimsReqId` staleness guard,
 *     `resetComboFaceAwSelections` (incl. the auto-size-OFF snap-to-largest rule),
 *     `_updateComboAwSelects` (= `render()`, the selects are declarative),
 *     `_restoreMissingSelectOption` (→ `ComboFaceView.aw_*_extra`),
 *     `comboSharedIdentityActive` — all in `comboLouverEngine.ts`.
 *   · the combo smartlist RENDER (`ComboSmartlistRows` in AccessorySelectorBody:
 *     static rows out of `cms.combo_smartlist`, the MANUFACTURER/SERIES/FRAME
 *     MATERIAL/BLADE heading set, the CLOSE button) + `applySmartlistPick`.
 *     Q12 respected: the per-part loader is bypassed entirely (no infinite scroll).
 *   · `recalcComboMasterLouverId` + the URL dedup cache +
 *     `awaitPendingComboLouverLookups` → `ComboFaceState.pendingLouverUrl`.
 *   · ★ Q2's ordering built AS LEGACY HAS IT (aw_dims BEFORE the resize on the
 *     smartlist-pick path). ★ Q5/R1's 500 is mirrored, not defended against.
 *
 * A1.3 — DONE (wave 2): the resize engine (`PATCH /louvers/<id>/with_position`).
 *   · `comboLouverEngine.ts`: `autoSize` / `recalc` (own 350 ms timer) / `ready` /
 *     `neverSized` / `shouldAutoSize` / `maybeAutoSize` (returns a Promise).
 *   · `AccessorySelector.tsx`: the combo merge in `getAccessoryData` + both race
 *     guards (`latestParams`, `latestEnclosure`) + the completion re-fire;
 *     `changeModelField`'s combo branch (airflow / depth / width|height /
 *     casing_mount_id / else) + the input-time CMS clamp;
 *     `changeParamsField`'s + `onPositionFieldChange`'s combo tails;
 *     `toggleComboAutoSize`; the combo arm of the auto-size BUTTON.
 *   · NOTE `CHANGEABLE_FIELDS.louver` needed NO change — legacy's
 *     `changeableFields` (:100-103) is identical for a combo; the combo fields ride
 *     an `Object.assign` in `getAccessoryData`, and `enclosure_*` are deliberately
 *     ABSENT from it (which is why an enclosure edit does not round-trip through
 *     `getAccessoryData`, only through `fetchAwDims` + `recalc`/`maybeAutoSize`).
 *
 * A1.4 — DONE (wave 3): handlers / validation / the Select gate.
 *   · `toggleComboFace` :1832 (incl. the truthy last-face guard) → §5.2(g) and
 *     `onComboFaceFieldChange` :1131 → §5.2(h) are `toggleFace` / `onFaceField`
 *     in `comboLouverEngine.ts`; `resetComboFace` :1512 is `reset` there
 *     (★ it is the trigger for Q5/R1 — the NEXT aw_dims 500s on the literal
 *     `"null"` — and for the stale APD).
 *   · `_applyComboValidation` :1038 → the engine's `applyValidation` (a DOM
 *     `.not-valid` pass, exactly as legacy: the enclosure depth/width/height
 *     inputs + the face rows).
 *   · `comboFacesSized` / `comboAnyFaceChecked` / `comboAnyFaceInvalid` /
 *     `comboIsSelectValid` above — ⚠ Q8 (`.some()` with auto-size ON) mirrored.
 *   · `updateComboFaceVelocity` :1174, `updateFaceVelocityColor` :1185 (the 101 %
 *     red rule) and `_setComboFaceInvalid` :1718 were already built by wave 2
 *     because the A1.2/A1.3 call chains needed them — A1.4 only CALLS them.
 *
 * A1.5 — DONE (wave 3): the How-to popup — `ComboLouverHowTo.tsx`
 *   (`combo_louver.how_to.tmpl` verbatim, `openComboLouverHowTo` :3374-3391),
 *   `createPortal`'d to `#ksdm-content` exactly where legacy appends it.
 *
 * A1.6 — DONE (wave 3): persist (`_persistCombo` :2667-2714) — the combo-only
 *   `PATCH /api/v1.0/ahu/positions/<id>` FIRST, then
 *   `PATCH /api/v1.0/ahu/louvers/<id>` with the full combo payload and
 *   deliberately NO `desired_face_velocity` (§4 W3); the board row's own
 *   `model_field` auto-save (§4 W4) is `LouverForm.tsx`'s `ComboEnclosureRow`,
 *   and ★ Q1's board-row `louver_params` write is there too.
 *
 * A2.1 — DONE (wave 4): redraw plumbing. `enclosure_width/height/depth` +
 *   stringified `combo_faces` (+ `is_combo`) joined `accessoryDrawSync.ts`'s
 *   canvasData snapshot (accessory_view.js:2058-2064) so a board-row enclosure
 *   edit redraws; `canvas3dGeometry.ts` gained the `:2181` depth fallback
 *   (★ SCOPED TO LOUVER — inventory R6 proves the legacy global form is a latent
 *   `NaN` for every other cube accessory), the `(diff_h || 0)` NaN guard
 *   (:2200-2201) and the unconditional `:2769-2784` extraShapes orphan sweep.
 *   The selector's mini preview injects the live per-face working state via
 *   `buildComboCanvasFaces` above + `useAccessoryPreviewCanvas`'s
 *   `ComboPreviewExtras` (`_updateCanvasPreview` :3415-3428/:3446).
 *
 * A2.2/A2.3 — DONE (wave 4): `drawComboLouverEnclosure`
 *   (canvas.view3d.js:3106-3305) in `canvas3dGeometry.ts` — the four combo
 *   branches of `drawAccessoriesCubeComplete`, the enclosure body (invisible box
 *   + dashed wireframe + hit mesh + wall opening + the two opacity-0.4 top/bottom
 *   walls) and the 1-3 face louvers at renderOrder 998 with blades at the
 *   hardcoded 3 in pitch. `ComboFaceState.active` + `.depth` above are what the
 *   face gate reads (canvas.view3d.js:3244-3251).
 *   ★ The left/right mirror matches canvas.view3d.js:3273, NOT
 *     models_accessory_louver.py:1533 — inventory R2, parked for the owner.
 * ======================================================================== */
